import { useRef, useState } from "react";
import { ScanQrCodeWidgetStyled } from "./styled";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setReduxWallet } from "store/reducers/walletReducer";
import { ROUTE_PATH } from "utils/constants/routePath";
import { userService } from "apiServices";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AlertControl } from "components/Modals/Alert";
import { Displays, Icons, Inputs, Modals } from "components";

const ScanQrCodeWidget = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authenRedux = useSelector((state) => state.authenRedux);
  const { t, i18n } = useTranslation(["menu", "common"]);

  const [_isModal, _setModal] = useState(false);
  const [_isBgLoading, _setIsBgLoading] = useState(false);
  // Scanner ยิง onScan ทุกเฟรมที่ยังเห็น qrcode ในกล้อง
  // ปิด modal เป็น setState (async) จึงกันไม่ทัน ต้องกันด้วย ref
  const _isHandledRef = useRef(false);

  const _handleClickModal = async () => {
    // จบทุกอย่างในนี้ ไม่ไล่ลูกค้าออกไปเบราว์เซอร์ข้างนอก —
    // QRScannerView เปิดกล้องสดให้ทุก browser และมีปุ่มคลังภาพเป็นทางสำรอง
    //
    // ไม่ใช้ liff.scanCodeV2() เพราะมันใช้ location.href = "liff://subwindow"
    // ซึ่งทำลาย LIFF auth state
    _isHandledRef.current = false;
    _setModal(true);
  };
  const _handleClickCloseModal = () => {
    _setModal(false);
  };

  const _successScan = (detectedCodes) => {
    if (_isHandledRef.current) return;
    if (detectedCodes && detectedCodes.length > 0) {
      _isHandledRef.current = true;
      _handleSubmit && _handleSubmit(detectedCodes[0].rawValue);
    }
  };

  const _handleSubmit = async (sn) => {
    _setIsBgLoading(true);

    // QR ของสินค้าบางตัวเก็บซีเรียลเปล่า ๆ ไม่ใช่ URL (เช่น "IKK019003-WW36280")
    // new URL() จะ throw ถ้าไม่ดักไว้ จอโหลดจะค้างจนปิดไม่ได้
    let url = null;
    try {
      url = new URL(sn);
    } catch {
      url = null;
    }

    // finally เพื่อให้จอโหลดถูกปิดทุกทางออก ไม่ว่าจะจบด้วย return หรือ error
    try {
      // กรณี URL แบบ ?redirect=...&id=... (เช่น QR จากระบบกิจกรรม)
      const redirect = url?.searchParams.get("redirect");
      const id = url?.searchParams.get("id");
      if (redirect && id) {
        _handleClickCloseModal();
        switch (redirect) {
          case "activity-qr-register":
            navigate(`${ROUTE_PATH.ACTIVITIES}/${id}/qr-register`);
            return;
          case "activity":
            navigate(`${ROUTE_PATH.ACTIVITIES}/${id}`);
            return;
          case "activity-scan":
            await _handleActivityScan(id, url.searchParams.get("code"));
            return;
          // QR ของ certificate ใบใดใบหนึ่งของ online form
          // ตัวหน้าปลายทางเช็คสิทธิ์เอง (ต้องลงฟอร์มนี้และแอดมินอนุมัติแล้ว)
          case "certificate-scan":
            navigate(`${ROUTE_PATH.ONLINE_FORM_CERTIFICATE_BASE}/${id}`);
            return;
          default:
            return;
        }
      }

      // กรณี path-based URL หรือซีเรียลเปล่า ๆ
      const split = sn.split("/");
      const serialNo = split[split.length - 1];
      switch (serialNo) {
        case "showroom":
          _handleShowRoomScan();
          _handleClickCloseModal();
          break;
        case "e-voucher":
          _handleEVoucherScan(split[split.length - 2]);
          _handleClickCloseModal();
          break;
        case "activity-qr-without-register":
          _handleClickCloseModal();
          break;
        default:
          let res =
            await userService.GET_PRODUCT_WARRANTEE_ACTIVE_BY_SN(serialNo);
          _handleClickCloseModal();
          if (res && res.status === 200) {
            navigate(`${ROUTE_PATH.PRODUCT_WARRANTEE}/${serialNo}`);
          } else {
            toast.error(t("Incorrect serial number", { ns: "common" }));
          }
          break;
      }
    } finally {
      _setIsBgLoading(false);
    }
  };

  // สแกน qrcode กิจกรรมจากเมนู SCAN ได้เลย ไม่ต้องเข้าหน้ากิจกรรมก่อน
  // ไม่ว่าผลจะเป็นยังไง สุดท้ายพาเข้าหน้ารายละเอียดกิจกรรมเสมอ
  // (สแกนยังไม่ครบ = เห็นจำนวนครั้งที่สแกนแล้ว / ครบแล้ว = เห็นผลว่าได้รางวัล
  //  / ยังไม่ได้เข้าร่วม = เข้าไปกดเข้าร่วมได้ทันที)
  const _handleActivityScan = async (activityId, qrcode) => {
    if (!qrcode) {
      navigate(`${ROUTE_PATH.ACTIVITIES}/${activityId}`);
      return;
    }
    const res = await userService.POST_ACTIVITY_HISTORY_SCAN({
      activity_id: activityId,
      qrcode: qrcode,
      member_type: authenRedux.member_type_active,
    });
    if (res && res.status === 200) {
      const scanned = res.data?.active_qrcode_amount;
      const target = res.data?.qrcode_passed_amount;
      toast.success(
        res.data?.is_condition_pass || !target
          ? t("Scan complete", { ns: "common" })
          : `${t("Scan complete", { ns: "common" })} (${scanned}/${target})`,
      );
      // ของรางวัลเป็น certificate → พาไปกรอกข้อมูลรับใบ ไม่ได้แต้มจึงไม่ต้องดึงยอด
      if (res.data?.is_certificate_pending) {
        navigate(`${ROUTE_PATH.ACTIVITIES}/${activityId}/certificate`);
        return;
      }
      // สแกนครบเงื่อนไขจะได้แต้มทันที ต้องดึงยอดใหม่ ไม่งั้นยอดที่โชว์ค้างของเดิม
      if (res.data?.is_condition_pass) {
        const resWallet = await userService.GET_MY_WALLET();
        if (resWallet && resWallet.status === 200) {
          dispatch(setReduxWallet(resWallet.data));
        }
      }
    } else if (res?.data?.error?.code === "ACTIVITY_NOT_JOINED") {
      toast.error(t("Please join the activity first", { ns: "common" }));
    } else if (res?.data?.error?.code === "QRCODE_ALREADY_SCANNED") {
      toast.error(t("You have already scanned this QR code", { ns: "common" }));
    } else {
      toast.error(t("Scan failed. Please check QrCode.", { ns: "common" }));
    }
    navigate(`${ROUTE_PATH.ACTIVITIES}/${activityId}`);
  };

  const _handleShowRoomScan = async () => {
    AlertControl.show({
      title: "ยืนยันการเข้าโชว์รูม",
      description: "คุณต้องการเชื่อมข้อมูลการเข้าใช้โขว์รูม ใช่หรือไม่",
      btnLabel1: "กลับ",
      btnLabel2: "ตกลง",
      onEvent2: async () => {
        _setIsBgLoading(true);
        let res = await userService.POST_SHOWROOM_SCAN_QR_BY_CUSTOMER();
        if (res && res.status === 200) {
          _setIsBgLoading(false);
          toast.success(t("Showroom check-in success", { ns: "common" }));
          navigate(ROUTE_PATH.HOME);
        } else {
          _setIsBgLoading(false);
          toast.error(
            "เชื่อมข้อมูลไม่สำเร็จ เนื่องจากไอดีของท่านถูกแบนหรือวันนี้มีการเชื่อมข้อมูลแล้ว กรุณาติดต่อเจ้าหน้าที่",
          );
        }
      },
    });
  };

  const _handleEVoucherScan = async (reward_id) => {
    AlertControl.show({
      title: "ยืนยันการรับของรางวัล",
      description: "คุณต้องการเข้าร่วมกิจกรรมใช่หรือไม่",
      btnLabel1: "กลับ",
      btnLabel2: "ตกลง",
      onEvent2: async () => {
        _setIsBgLoading(true);
        let params = {
          reward_id,
          member_type: authenRedux.member_type_active,
        };
        let res = await userService.POST_REWARD_E_VOUCHER_SCAN(params);
        if (res && res.status === 200) {
          _setIsBgLoading(false);
          toast.success(t("Activity joined successfully", { ns: "common" }));
          navigate(ROUTE_PATH.HOME);
        } else {
          _setIsBgLoading(false);
          toast.error(
            t("Registration failed. Please check conditions.", {
              ns: "common",
            }),
          );
        }
      },
    });
  };

  return (
    <>
      <Displays.BGLoading visible={_isBgLoading} />
      <ScanQrCodeWidgetStyled lang={i18n.language}>
        <div className="scqr_icon_wrap" onClick={_handleClickModal}>
          <div className="btn_scan">
            <Icons.MenuBarScan />
          </div>
        </div>
        <div className={`scqr_label`}>{t("SCAN", { ns: "menu" })}</div>
      </ScanQrCodeWidgetStyled>
      <Modals.BodyEmpty
        isShowModal={_isModal}
        onCloseModal={_handleClickCloseModal}
      >
        <Inputs.QRScannerView
          onScan={_successScan}
          onClose={_handleClickCloseModal}
        />
          {/* <button
            type="button"
            style={{
              position: "absolute",
              left: 20,
              bottom: 20,
              color: "white",
              background: "red",
            }}
            // onClick={() => _successScan("IKK019003-WW36280")}
            // e-voucher
            // onClick={() =>
            //   _successScan({ text: "/6809299e5ddd8f8ef9b4075d/e-voucher" })
            // }

            // onClick={() =>
            //   _successScan({
            //     text: "https://siammusicmembers.com/product-warrantee/IKX170503-ZR08340",
            //   })
            // }

            onClick={() =>
              _successScan({
                text: "https://siammusicmembers.com/69ba5d09a426d81b31420055/activity-qr-without-register",
              })
            }
          >
            Test
          </button> */}
      </Modals.BodyEmpty>
    </>
  );
};

export default ScanQrCodeWidget;
